/**
 * Built-in tags (SPEC §5.9, D11): shipped with fields, editable like any tag. Their ids are fixed,
 * so code can refer to them and every device agrees; the server creates them once, through the op
 * log, if they are missing.
 */
import type { NodeId } from './ids.ts'
import { makeNode, type Node } from './model.ts'
import { SCHEMA_FIELD, type FieldDef, type Schema } from './schema.ts'

const id = (n: number): NodeId => `5a1a7a90-0000-8000-8000-${n.toString(16).padStart(12, '0')}`

export const TAG = {
  meeting: id(1),
  person: id(2),
  company: id(3),
  task: id(4),
  project: id(5),
  highlight: id(6),
  aiMemory: id(7),
} as const

/**
 * The settings node: an untitled page (shown nowhere) whose fields hold settings that follow you
 * across devices (`editor`: SPEC §6.3 behaviour options, D45). Created with the built-in tags.
 */
export const SETTINGS_ID: NodeId = id(0x100)
export const SETTINGS_EDITOR = 'editor'

/** Field ids of the task tag that code reads. */
export const TASK_FIELD = { status: 'task.status', due: 'task.due', assignee: 'task.assignee', priority: 'task.priority', source: 'task.source' } as const

const schema = (fields: FieldDef[], ext: NodeId[] = []): Schema => ({ fields, extends: ext })

export const BUILTIN_TAGS: readonly { readonly id: NodeId; readonly title: string; readonly schema: Schema }[] = [
  {
    id: TAG.meeting,
    title: 'meeting',
    schema: schema([
      { id: 'meeting.date', name: 'Date', type: 'datetime', required: true },
      { id: 'meeting.attendees', name: 'Attendees', type: 'ref', of: TAG.person, multi: true },
      { id: 'meeting.company', name: 'Company', type: 'ref', of: TAG.company },
      { id: 'meeting.link', name: 'Link', type: 'url' },
    ]),
  },
  {
    id: TAG.person,
    title: 'person',
    schema: schema([
      { id: 'person.company', name: 'Company', type: 'ref', of: TAG.company },
      { id: 'person.role', name: 'Role', type: 'text' },
      { id: 'person.email', name: 'Email', type: 'email' },
      { id: 'person.phone', name: 'Phone', type: 'phone' },
      { id: 'person.birthday', name: 'Birthday', type: 'date' },
      { id: 'person.relationship', name: 'Relationship', type: 'select', options: ['client', 'partner', 'colleague', 'friend', 'family', 'other'] },
    ]),
  },
  {
    id: TAG.company,
    title: 'company',
    schema: schema([
      { id: 'company.type', name: 'Type', type: 'select', options: ['own', 'agency client', 'end client', 'vendor', 'partner', 'personal'] },
      { id: 'company.via', name: 'Via', type: 'ref', of: TAG.company },
      { id: 'company.domain', name: 'Domain', type: 'text' },
      { id: 'company.status', name: 'Status', type: 'select', options: ['active', 'prospect', 'paused', 'past'] },
      { id: 'company.notes', name: 'Notes', type: 'text' },
    ]),
  },
  {
    id: TAG.task,
    title: 'task',
    schema: schema([
      { id: TASK_FIELD.status, name: 'Status', type: 'select', options: ['todo', 'doing', 'done', 'cancelled'], hidden: true },
      { id: TASK_FIELD.due, name: 'Due', type: 'date' },
      { id: TASK_FIELD.assignee, name: 'Assignee', type: 'ref', of: TAG.person },
      { id: TASK_FIELD.priority, name: 'Priority', type: 'select', options: ['high', 'medium', 'low'] },
      { id: TASK_FIELD.source, name: 'Source', type: 'ref' },
    ]),
  },
  {
    id: TAG.project,
    title: 'project',
    schema: schema([
      { id: 'project.company', name: 'Company', type: 'ref', of: TAG.company },
      { id: 'project.status', name: 'Status', type: 'select', options: ['active', 'paused', 'done'] },
      { id: 'project.due', name: 'Due', type: 'date' },
      { id: 'project.owner', name: 'Owner', type: 'ref', of: TAG.person },
    ]),
  },
  {
    id: TAG.highlight,
    title: 'highlight',
    schema: schema([
      { id: 'highlight.quote', name: 'Quote', type: 'text' },
      { id: 'highlight.page', name: 'Page', type: 'number' },
      { id: 'highlight.colour', name: 'Colour', type: 'select', options: ['yellow', 'green', 'blue', 'red'] },
      { id: 'highlight.asset', name: 'Asset', type: 'ref' },
    ]),
  },
  { id: TAG.aiMemory, title: 'ai-memory', schema: schema([]) },
]

/** The built-in tag nodes and the settings node, for seeding. */
export function builtinNodes(now: number): Node[] {
  return [
    ...BUILTIN_TAGS.map((t) =>
      makeNode({ id: t.id, kind: 'tag', title: t.title, props: { [SCHEMA_FIELD]: t.schema }, origin: 'user', createdAt: now, updatedAt: now }),
    ),
    makeNode({ id: SETTINGS_ID, kind: 'page', createdAt: now, updatedAt: now }),
  ]
}
